import type { Command } from "../../core/commands/Command";
import { History } from "../../core/commands/History";
import { ApplyLayerCompCommand } from "../../core/commands/ApplyLayerCompCommand";
import type { LayerCompPatch } from "../../core/commands/ApplyLayerCompCommand";
import type { Document } from "../../core/document/Document";
import type { Layer } from "../../core/document/Layer";
import type { Viewport } from "../../core/render/Viewport";
import type { RGB, Tool } from "../../core/tools/Tool";
import type { ActionStep } from "../../core/actions/ActionStep";

/** Callback signature for the Actions recorder. */
export type ActionRecorder = (step: ActionStep) => void;

export class EditorState {
  private _doc: Document | null = null;
  // The committed document as of the last history operation, snapshotted the
  // moment a live-drag preview first mutates `_doc` via applyTransient(). A
  // subsequent committed execute() uses THIS as the history baseline instead of
  // the preview-polluted `_doc`, so a command's inverse captures the true
  // pre-gesture state. Without it, e.g. a Marquee drag (transient preview then
  // committed SetSelectionCommand at pointer-up) recorded the preview selection
  // as its "before", so undo restored the drag rectangle instead of clearing
  // the selection. Reset by every history op that establishes a new baseline.
  private _transientBase: Document | null = null;
  private _viewport: Viewport = { panX: 0, panY: 0, zoom: 1 };
  private _activeTool: Tool | null = null;
  private _fgColor: RGB = { r: 0, g: 0, b: 0 };
  private _bgColor: RGB = { r: 255, g: 255, b: 255 };
  /**
   * Per-document History instances keyed by document `id` (#209) -- NOT by
   * `name`. Each open document has its own independent undo/redo stack
   * (#183). Keying by name used to mean two open documents sharing a display
   * name (two "Untitled-1"s, or a doc renamed to collide with another) would
   * silently share -- and corrupt -- a single undo stack. See `_keyFor()`.
   * The active history is always _docHistories.get(_keyFor(_doc)) ?? _sharedHistory.
   */
  private readonly _docHistories = new Map<string, History>();
  /**
   * Counter used to mint fallback keys (see `_keyFor`) for documents that
   * were constructed without a real `id` (#209).
   */
  private _fallbackIdCounter = 0;
  /**
   * Preferences > Performance > History States cap, applied to every
   * document's History (#148) -- not just the currently-active one.
   * Updated via setMaxHistoryStates() and consumed by createHistory()
   * whenever a new per-document History is constructed.
   */
  private _maxHistoryStatesPref = 50;
  /**
   * Fallback history used when no document is open (e.g. the very first
   * LoadDocumentCommand before a name is known).
   */
  private readonly _sharedHistory = new History(this._maxHistoryStatesPref);
  private readonly _subscribers = new Set<() => void>();
  private _quickMask = false;
  private _dirty = false;
  private _savedPath: string | null = null;
  private _savedFormat: string | null = null;
  private _selectedLayerIds: Set<string> = new Set();
  private _layerFilter = "";
  private _brushPresets: Map<string, { pixels: Uint8ClampedArray; width: number; height: number; hotspotX: number; hotspotY: number }> = new Map();
  private _patternPresets: Map<string, { pixels: Uint8ClampedArray; width: number; height: number; name: string }> = new Map();
  private _layerComps: Array<{ id: string; name: string; visibility: Map<string, boolean>; positions: Map<string, { x: number; y: number }>; thumbnail?: string }> = [];
  private _workspaces: Map<string, { panels: string[]; workspace: string }> = new Map();
  private _autoSelect = false;
  private _autoSelectMode: "layer" | "group" = "layer";
  private _showTransformControls = false;
  /** When non-null, every non-transient command dispatched via execute() is
   *  reported to this callback as a serialisable ActionStep. */
  private _actionRecorder: ActionRecorder | null = null;

  get workspaces(): ReadonlyMap<string, { panels: string[]; workspace: string }> { return this._workspaces; }

  saveWorkspace(name: string, panels: string[]): void {
    this._workspaces.set(name, { panels, workspace: name });
    this._persistWorkspaces();
    this.notify();
  }

  loadWorkspace(name: string): { panels: string[] } | undefined {
    return this._workspaces.get(name);
  }

  deleteWorkspace(name: string): void {
    this._workspaces.delete(name);
    this._persistWorkspaces();
    this.notify();
  }

  resetWorkspaces(): void {
    this._workspaces.clear();
    this._initDefaultWorkspaces();
    this._persistWorkspaces();
    this.notify();
  }

  private _initDefaultWorkspaces(): void {
    this._workspaces.set("Essentials", {
      panels: ["layers", "color", "swatches", "properties"],
      workspace: "Essentials",
    });
    this._workspaces.set("Photography", {
      panels: ["layers", "histogram", "navigator", "adjustments", "properties"],
      workspace: "Photography",
    });
    this._workspaces.set("Painting", {
      panels: ["layers", "brush-settings", "color", "swatches", "navigator"],
      workspace: "Painting",
    });
    this._workspaces.set("Web", {
      panels: ["layers", "properties", "info", "navigator", "styles"],
      workspace: "Web",
    });
  }

  private _persistWorkspaces(): void {
    try {
      const data: Record<string, { panels: string[]; workspace: string }> = {};
      this._workspaces.forEach((v, k) => { data[k] = v; });
      localStorage.setItem("pixelpa-workspaces", JSON.stringify(data));
    } catch { /* ignore */ }
  }

  private _loadPersistedWorkspaces(): void {
    try {
      const raw = localStorage.getItem("pixelpa-workspaces");
      if (raw) {
        const data = JSON.parse(raw);
        for (const [k, v] of Object.entries(data)) {
          this._workspaces.set(k, v as { panels: string[]; workspace: string });
        }
      }
    } catch { /* ignore */ }
    if (this._workspaces.size === 0) {
      this._initDefaultWorkspaces();
    }
  }

  constructor() {
    this._loadPersistedWorkspaces();
  }

  /**
   * Construct a new History pre-configured with the current
   * History-States preference (#148), so every document's undo stack
   * (not just the active one) is capped consistently.
   */
  private createHistory(): History {
    return new History(this._maxHistoryStatesPref);
  }

  /**
   * Resolve the stable per-document key used to index `_docHistories` (#209).
   *
   * Prefers the document's own `id`, which is set once at creation and then
   * preserved across every later Command application -- Commands build the
   * next Document via `{ ...doc, ...patch }`, which copies `id` forward
   * along with every other field it doesn't explicitly override.
   *
   * Documents from call sites that don't set `id` yet (older construction
   * sites; tracked as a follow-up) get a fallback key generated the first
   * time they're seen here, written directly onto the document object so it
   * is likewise carried forward by every later spread -- instead of being
   * silently re-derived (and mistaken for a distinct "new" document) on
   * every access. Mutating `id` in place after the fact is a deliberate,
   * narrow exception to "the document is immutable" (`id` is `readonly`
   * only at the type level), matching the existing pixel-buffer exception
   * noted at the top of Document.ts -- it fills in a field that should have
   * been set at construction, it never overwrites an existing value, and it
   * happens at most once per document lineage.
   */
  private _keyFor(doc: Document): string {
    if (doc.id) return doc.id;
    const fallback = `doc-fallback-${++this._fallbackIdCounter}`;
    (doc as { id?: string }).id = fallback;
    return fallback;
  }

  /**
   * Return the History instance for the active document.
   * Each document `id` maps to its own History (#183, #209).
   */
  private get _history(): History {
    if (this._doc) {
      const key = this._keyFor(this._doc);
      if (!this._docHistories.has(key)) {
        this._docHistories.set(key, this.createHistory());
      }
      return this._docHistories.get(key)!;
    }
    return this._sharedHistory;
  }

  /**
   * Update the History-States preference (Preferences > Performance) and
   * apply it immediately to EVERY open document's History, plus the
   * no-document fallback history and all future ones created via
   * createHistory() (#148). This is the entry point Preferences should
   * call instead of `state.history.setMaxStates(n)`, which only ever
   * mutated the currently-active document's History instance.
   */
  setMaxHistoryStates(n: number): void {
    const clamped = Math.max(1, n);
    this._maxHistoryStatesPref = clamped;
    for (const history of this._docHistories.values()) {
      history.setMaxStates(clamped);
    }
    this._sharedHistory.setMaxStates(clamped);
  }

  /** The currently-configured History-States preference (default 50). */
  get maxHistoryStates(): number {
    return this._maxHistoryStatesPref;
  }

  get doc(): Document | null { return this._doc; }
  get viewport(): Viewport { return this._viewport; }
  get activeTool(): Tool | null { return this._activeTool; }
  /** Returns the active document's history. */
  get history(): History { return this._history; }
  get fgColor(): RGB { return this._fgColor; }
  get bgColor(): RGB { return this._bgColor; }
  get quickMask(): boolean { return this._quickMask; }
  get dirty(): boolean { return this._dirty; }
  get savedPath(): string | null { return this._savedPath; }
  setSavedPath(path: string | null): void { this._savedPath = path; }
  /** The format key ('psd', 'png', 'jpeg', 'webp', etc.) used the last time
   *  Save As / open set savedPath.  Ctrl+S and the dirty-close prompt use this
   *  to re-save in the original format rather than always defaulting to PNG. */
  get savedFormat(): string | null { return this._savedFormat; }
  setSavedFormat(fmt: string | null): void { this._savedFormat = fmt; }
  markSaved(): void { this._dirty = false; this.notify(); }
  setQuickMask(enabled: boolean): void {
    this._quickMask = enabled;
    this.notify();
  }

  execute(cmd: Command): void {
    if (this._doc) {
      // #209: _history is keyed by the document's `id` (via _keyFor()), which
      // a rename command does not change, so -- unlike the old name-keyed
      // scheme -- no post-execute migration is needed to keep the History
      // entry reachable after a rename (#199 is handled for free).
      //
      // Use the pre-transient baseline (if a live-drag preview is in progress)
      // as the history input so the command's inverse captures the true
      // pre-gesture state rather than the preview-polluted `_doc`. Then clear it
      // — this committed command re-establishes the baseline.
      const base = this._transientBase ?? this._doc;
      this._transientBase = null;
      this._doc = this._history.execute(base, cmd);
      this._dirty = true;
    } else {
      this._transientBase = null;
      this._doc = cmd.apply(emptyDoc());
    }

    // Notify the Actions recorder for every non-transient command.
    if (this._actionRecorder && !cmd.transient) {
      const step: ActionStep = cmd.toActionStep
        ? cmd.toActionStep()
        : {
            id: crypto.randomUUID(),
            name: cmd.kind,
            command: cmd.kind,
            params: {},
            recordable: false,
          };
      this._actionRecorder(step);
    }

    this.notify();
  }

  applyTransient(cmd: Command): void {
    if (!this._doc) return;
    // Snapshot the committed doc the first time a preview mutates it, so the
    // eventual committed execute() can restore it as the history baseline.
    if (this._transientBase === null) this._transientBase = this._doc;
    this._doc = cmd.apply(this._doc);
    this.notify();
  }

  undo(): void {
    if (!this._doc) return;
    this._transientBase = null;
    this._doc = this._history.undo(this._doc);
    this.notify();
  }

  redo(): void {
    if (!this._doc) return;
    this._transientBase = null;
    this._doc = this._history.redo(this._doc);
    this.notify();
  }

  stepBackward(): void {
    if (!this._doc) return;
    this._transientBase = null;
    this._doc = this._history.stepBackward(this._doc);
    this.notify();
  }

  stepForward(): void {
    if (!this._doc) return;
    this._transientBase = null;
    this._doc = this._history.stepForward(this._doc);
    this.notify();
  }

  setDocument(doc: Document | null): void {
    this._transientBase = null;
    this._doc = doc;
    this._dirty = false;
    this.notify();
  }

  /**
   * Switch the active document without pushing a LoadDocumentCommand (#183).
   * The incoming document already has its own History in _docHistories
   * (keyed by `id` via _keyFor(), #209 -- so two open documents that happen
   * to share a `name` still resolve to independent stacks). After switching,
   * undo/redo operates on that document's own stack.
   */
  switchDocument(doc: Document): void {
    this._transientBase = null;
    this._doc = doc;
    // Initialise a history for this doc if it doesn't have one yet.
    const key = this._keyFor(doc);
    if (!this._docHistories.has(key)) {
      this._docHistories.set(key, this.createHistory());
    }
    this.notify();
  }

  /**
   * Drop the history for a closed document (frees memory, #183).
   * Call this after removing the document from the open-documents registry.
   *
   * #209: per-document History is now keyed by `id`, not `name` -- pass the
   * Document itself so the correct key can be resolved (and, if it never had
   * an `id`, so the same fallback-key derivation `_keyFor` used elsewhere is
   * applied consistently). A raw string is still accepted so existing
   * call sites that only have the document's `name` on hand keep compiling;
   * update them to pass the Document to actually free the right entry.
   */
  dropDocumentHistory(doc: Document | string): void {
    const key = typeof doc === "string" ? doc : this._keyFor(doc);
    this._docHistories.delete(key);
  }

  /**
   * Migrate the history to a new key after a rename (#199).
   *
   * #209: since `_docHistories` is now keyed by the document's stable `id`
   * (which a rename does not change), this is no longer needed for renames
   * performed via `execute()` -- the same History entry stays reachable
   * automatically. Kept for backward compatibility with existing call sites
   * that migrate by name; such calls are now a harmless no-op once the
   * document has a real (or fallback) `id`, since `oldName` will no longer
   * match any key in `_docHistories`.
   */
  migrateDocumentHistory(oldName: string, newName: string): void {
    const existing = this._docHistories.get(oldName);
    if (existing) {
      this._docHistories.set(newName, existing);
      this._docHistories.delete(oldName);
    }
  }

  setViewport(patch: Partial<Viewport>): void {
    this._viewport = { ...this._viewport, ...patch };
    this.notify();
  }

  setActiveTool(tool: Tool): void {
    // Give the outgoing tool a chance to commit any in-progress session
    // (e.g. TypeTool commits text) before the new tool takes over.
    this._activeTool?.onDeactivate?.();
    this._activeTool = tool;
    this.notify();
  }

  setForegroundColor(c: RGB): void {
    this._fgColor = c;
    this.notify();
  }

  setBackgroundColor(c: RGB): void {
    this._bgColor = c;
    this.notify();
  }

  swapColors(): void {
    const fg = this._fgColor;
    this._fgColor = this._bgColor;
    this._bgColor = fg;
    this.notify();
  }

  resetColors(): void {
    this._fgColor = { r: 0, g: 0, b: 0 };
    this._bgColor = { r: 255, g: 255, b: 255 };
    this.notify();
  }

  requestRender(): void {
    this.notify();
  }

  setActiveLayer(id: string | null): void {
    if (!this._doc) return;
    this._doc = { ...this._doc, activeLayerId: id };
    this._selectedLayerIds.clear();
    this.notify();
  }

  getDocumentAtStateIndex(index: number): Document | null {
    if (!this._doc) return null;
    return this._history.getDocumentAt(this._doc, index);
  }

  getHistoryStateCount(): number {
    return this._history.getPastEntries().length + 1;
  }

  get selectedLayerIds(): ReadonlySet<string> { return this._selectedLayerIds; }

  setSelectedLayerIds(ids: Set<string> | null): void {
    this._selectedLayerIds = ids ? new Set(ids) : new Set();
    this.notify();
  }

  toggleLayerSelection(id: string): void {
    if (this._selectedLayerIds.has(id)) {
      this._selectedLayerIds.delete(id);
    } else {
      this._selectedLayerIds.add(id);
    }
    this.notify();
  }

  get layerFilter(): string { return this._layerFilter; }
  set layerFilter(text: string) { this._layerFilter = text; this.notify(); }

  get autoSelect(): boolean { return this._autoSelect; }
  set autoSelect(value: boolean) { this._autoSelect = value; this.notify(); }
  // Move tool "Auto-Select" target granularity (options bar Layer/Group select).
  get autoSelectMode(): "layer" | "group" { return this._autoSelectMode; }
  set autoSelectMode(value: "layer" | "group") { this._autoSelectMode = value; this.notify(); }

  get showTransformControls(): boolean { return this._showTransformControls; }
  set showTransformControls(value: boolean) { this._showTransformControls = value; this.notify(); }

  get brushPresets(): ReadonlyMap<string, { pixels: Uint8ClampedArray; width: number; height: number; hotspotX: number; hotspotY: number }> { return this._brushPresets; }
  addBrushPreset(name: string, pixels: Uint8ClampedArray, width: number, height: number, hotspotX: number, hotspotY: number): void {
    this._brushPresets.set(name, { pixels: new Uint8ClampedArray(pixels), width, height, hotspotX, hotspotY });
    this.notify();
  }

  get patternPresets(): ReadonlyMap<string, { pixels: Uint8ClampedArray; width: number; height: number; name: string }> { return this._patternPresets; }
  addPatternPreset(id: string, name: string, pixels: Uint8ClampedArray, width: number, height: number): void {
    this._patternPresets.set(id, { pixels: new Uint8ClampedArray(pixels), width, height, name });
    this.notify();
  }

  get layerComps(): ReadonlyArray<{ id: string; name: string; visibility: Map<string, boolean>; positions: Map<string, { x: number; y: number }>; thumbnail?: string }> { return this._layerComps; }

  createLayerComp(name: string): void {
    if (!this._doc) return;
    const visibility = new Map<string, boolean>();
    const positions = new Map<string, { x: number; y: number }>();
    const collect = (layers: readonly Layer[]) => {
      for (const l of layers) {
        visibility.set(l.id, l.visible);
        positions.set(l.id, { x: l.x, y: l.y });
        if (l.kind === "group") collect(l.children);
      }
    };
    collect(this._doc.layers);
    this._layerComps.push({ id: crypto.randomUUID(), name, visibility, positions });
    this.notify();
  }

  updateLayerComp(id: string): void {
    if (!this._doc) return;
    const comp = this._layerComps.find(c => c.id === id);
    if (!comp) return;
    comp.visibility.clear();
    comp.positions.clear();
    const collect = (layers: readonly Layer[]) => {
      for (const l of layers) {
        comp.visibility.set(l.id, l.visible);
        comp.positions.set(l.id, { x: l.x, y: l.y });
        if (l.kind === "group") collect(l.children);
      }
    };
    collect(this._doc.layers);
    this.notify();
  }

  applyLayerComp(id: string): void {
    if (!this._doc) return;
    const comp = this._layerComps.find(c => c.id === id);
    if (!comp) return;
    const patches: LayerCompPatch[] = [];
    const collect = (layers: readonly Layer[]) => {
      for (const l of layers) {
        const patch: { layerId: string; visible?: boolean; x?: number; y?: number } = { layerId: l.id };
        let hasDiff = false;
        const vis = comp.visibility.get(l.id);
        if (vis !== undefined && vis !== l.visible) {
          patch.visible = vis;
          hasDiff = true;
        }
        const pos = comp.positions.get(l.id);
        if (pos !== undefined && (pos.x !== (l as any).x || pos.y !== (l as any).y)) {
          patch.x = pos.x;
          patch.y = pos.y;
          hasDiff = true;
        }
        if (hasDiff) patches.push(patch as LayerCompPatch);
        if (l.kind === "group") collect(l.children);
      }
    };
    collect(this._doc.layers);
    if (patches.length > 0) {
      this.execute(new ApplyLayerCompCommand(comp.name, patches));
    }
  }

  deleteLayerComp(id: string): void {
    this._layerComps = this._layerComps.filter(c => c.id !== id);
    this.notify();
  }

  /**
   * Attach a recorder callback. Every subsequent non-transient Command passed
   * to execute() will call `recorder` with an ActionStep descriptor.
   */
  setActionRecorder(recorder: ActionRecorder): void {
    this._actionRecorder = recorder;
  }

  /** Detach the active recorder (called when the user presses Stop). */
  clearActionRecorder(): void {
    this._actionRecorder = null;
  }

  subscribe(fn: () => void): () => void {
    this._subscribers.add(fn);
    return () => this._subscribers.delete(fn);
  }

  private notify(): void {
    for (const fn of this._subscribers) fn();
  }
}

function emptyDoc(): Document {
  return {
    name: "Untitled",
    width: 0,
    height: 0,
    dpi: 72,
    layers: [],
    activeLayerId: null,
    selection: null,
  };
}




